import { FC, ReactNode, useEffect, useRef, useState } from "react";
import styles from "./TaskCard.module.scss";
import currencyBlueIcon from "../../icons/currency-blue.png";
import { formatNumberWithSpaces } from "../../utils/numbers";
import { omit } from 'lodash';
import { TicketsPanel } from "../TicketsPanel/TicketsPanel";
import { getTimeLeft, now } from "../../utils/time";
import { useSelector } from "../../hooks";
import redArrowDown from "../../icons/red_arrow_down.png";

export interface ITaskReward {
    balance: number;
    regularTickets?: number,
    rareTickets?: number,
    epicTickets?: number,
    legendaryTickets?: number;
}

export const hasRecurringRewards = (reward: ITaskReward) => {
    const { balance, ...tickets } = reward;
    return Object.values(tickets).some(value => value && value > 0);
};

export interface ITask {
    image: string;
    title: string;
    description: string;
    reward: ITaskReward;
    lastRewardDate?: number;
    pendingDate?: number;
}

export type JoinChannelTaskCardType = 'new' | 'active' | 'archive' | 'inactive';

interface ITaskCardProps {
    task: ITask;
    index: number;
    type?: JoinChannelTaskCardType;
    performButton: ReactNode;
    rewardButton?: ReactNode;
    avatarFallbackToTitle?: boolean;
}

export const TaskCard: FC<ITaskCardProps> = ({ task, index, type, performButton, rewardButton, avatarFallbackToTitle }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [needsExpand, setNeedsExpand] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);
    const descriptionRef = useRef<HTMLDivElement>(null);

    const penalty = useSelector(store => store.tasks.joinChannelTasks?.penalty?.penalties);

    const getOnceProfitLabel = () => type === 'new' ? 'Доход за выполнение' : 'Начислено';

    const getRewardValue = () => {
        let rewardValue = task.reward.balance;
        const hasPenalty = task.pendingDate && penalty?.reward;

        if (hasPenalty)
            rewardValue = Math.round(rewardValue * (1 - penalty.reward));

        return (
            <div>
                <span>{ formatNumberWithSpaces(rewardValue)}</span>
                { hasPenalty && <img className={ styles.downArrow} src={ redArrowDown } alt='arrow' /> }
            </div>
        )
    }

    const getDailyProfitLabel = () => {
        const idDisabled = type === 'archive' || type === 'inactive';

        const getNextRewardDate = (lastRewardDate: number) => lastRewardDate - now()  + 86400 > 0
            ? <>Начислим через<br />{ getTimeLeft(lastRewardDate + 86400, -1) }</>
            : 'Производим начисление...'

        const text = idDisabled
            ? 'Начисление остановлено'
            : (task.lastRewardDate
                ? <span>{getNextRewardDate(task.lastRewardDate)}</span>
                : 'Доход в сутки'
            );
        return <div className={ `${styles.label} ${idDisabled ? styles.warn : ''}` }>{ text }</div>;
    }

    useEffect(() => {
        if (descriptionRef.current) {
            const element = descriptionRef.current;
            const isTextClamped = element.scrollHeight > element.clientHeight;
            setNeedsExpand(isTextClamped);
        }
    }, [task.description]);

    useEffect(() => {
        setImageFailed(false);
    }, [task.image]);

    const hasImage = Boolean(task.image?.trim());
    const showTitleAvatar = avatarFallbackToTitle && (!hasImage || imageFailed);

    const getAvatar = () => {
        if (showTitleAvatar) {
            return (
                <div className={`${styles.avatar} ${styles.avatarLetter}`}>
                    {task.title?.substring(0, 1).toUpperCase() ?? ''}
                </div>
            );
        }

        return (
            <img
                src={task.image}
                alt="Avatar"
                className={styles.avatar}
                onError={() => avatarFallbackToTitle && setImageFailed(true)}
            />
        );
    };

    const getDescription = () => {
        return (
            <div className={styles.descriptionWrapper}>
                <div 
                    ref={descriptionRef}
                    className={`${styles.description} ${isExpanded ? styles.expanded : ''}`}
                >
                    {task.description.split('\n').map((p, index) => (
                        <p key={`descriptionP${index}`}>{p}</p>
                    ))}
                </div>
                
                {needsExpand && !isExpanded && (
                    <button 
                        className={styles.readMoreBtn}
                        onClick={() => setIsExpanded(true)}
                    >
                        Подробнее
                    </button>
                )}
            </div>
        );
    }

    const getWeeklyReward = () => {
        const { balance, ...rest } = task.reward;
        const weeklyReward = Object.fromEntries(
            Object.entries(rest).map(([key, value]) => [key, value * 7])
        ) as Omit<ITaskReward, 'balance'>;

        return weeklyReward;
    }

    const hasTicketRewards = hasRecurringRewards(task.reward);
    
    return (
        <div className={`${styles.card} ${index & 1 ? styles.odd : styles.even}`}>
            <div className={styles.header}>
                { getAvatar() }
                
                <div className={styles.titleBlock}>
                    <h2 className={styles.title}>{task.title}</h2>
                </div>
            </div>

            <div className={`${styles.incomeBlock} ${index & 1 ? styles.odd : styles.even} ${!hasTicketRewards ? styles.withBottomSpacing : ''}`}>
                <span className={styles.incomeLabel}>{ getOnceProfitLabel() }</span>
                
                <div className={styles.incomeValue}>
                    <img
                        src={currencyBlueIcon}
                        alt="Currency"
                        className={styles.currencyIcon}
                    />
                    <span>{ getRewardValue() }</span>
                </div>
            </div>

            { hasTicketRewards && (
                <div className={styles.ticketPanelsCont}>
                    <TicketsPanel
                        label={ getDailyProfitLabel() }
                        background={true}
                        {...omit(task.reward, 'balance')}
                        context='small'
                    />

                    <TicketsPanel
                        label={ <div className={ styles.label }>Доход в неделю</div> }
                        background={true} { ...getWeeklyReward() }
                        context='small'
                    />
                </div>
            ) }

            { getDescription() }

            { type === "active" && <div className={ `${styles.desc} ${styles.active}` }>(подписан, приносит доход)</div> }
            { type === "inactive" && <div className={ `${styles.desc} ${styles.archive}` }>(отписались, начисление остановлено)</div> }
            { type === "archive" && <div className={ `${styles.desc} ${styles.archive}` }>(реклама завершена, начисление остановлено)</div> }

            { rewardButton }
            { performButton }            
        </div>
    );
};